import { create, Styles, SheetsManager, StyleSheet, Jss } from "jss";

import preset from "jss-preset-default";
import {
  watch,
  Ref,
  ref,
  onUnmounted,
  computed,
  onBeforeMount,
  provide,
  inject
} from "vue";

export function useReactiveProps<U>(props: U) {
  return computed(() => props);
}

// default jss and sheet manager
const jss = create(preset());
const sheetsManager = new SheetsManager();

// setup jss context (provider and injector)
interface JssContext {
  manager: SheetsManager;
  jss: Jss;
  insertionPoint?: HTMLElement | undefined;
}

const jssSymbol = Symbol("jssProvider");

export function createJssContext({
  insertionPoint
}: {
  insertionPoint?: HTMLElement;
}): JssContext {
  const jss = create({ ...preset(), insertionPoint });
  const manager = new SheetsManager();

  return { jss, manager, insertionPoint };
}

export function provideJssContext(ctx: JssContext) {
  provide<JssContext>(jssSymbol, ctx);
}

export function injectJssContext(): JssContext {
  const result = inject<JssContext>(jssSymbol);

  if (result) {
    return result;
  }

  return { jss, manager: sheetsManager };
}

interface StyleData {
  [x: string]: string | number | boolean | undefined | StyleData;
}

/**
 * This factory is used to manage stylesheets intelligently. Using a single stylesheet
 * for static rules, and scoping rules to components.
 * @param styles
 */
export function createUseStyles<
  Name extends string | number | symbol,
  PropType extends StyleData
>(styles: Partial<Styles<Name>>) {
  return function useStyles(reactiveData?: Ref<PropType>) {
    const { jss } = injectJssContext();

    const sheet = jss.createStyleSheet(styles, { link: true });
    const classes = ref(sheet.classes);

    onBeforeMount(() => {
      sheet.attach();
    });

    if (reactiveData) {
      sheet.update(reactiveData.value);
      watch(reactiveData, newValue => sheet.update(newValue), { deep: true });
    }

    onUnmounted(() => {
      jss.removeStyleSheet(sheet as StyleSheet<string>);
    });

    return classes;
  };
}

/**
 * Use multiple instances of a style object, by passing a ref to an array of values
 * as the data.
 * @param styles
 * @param data
 */
export function useStyleArray<
  Name extends string | number | symbol,
  T extends StyleData
>(styles: Partial<Styles<Name>>, data: Ref<readonly T[]>) {
  const { jss } = injectJssContext();
  type SheetRef = { sheet: StyleSheet<Name> };

  const sheetArray: Ref<SheetRef[]> = ref([]);

  function updateData(newData: readonly T[]): void {
    const dataLength = newData.length;
    const lengthDiff = sheetArray.value.length - dataLength;

    // first make sure we have the right number of stylesheets
    if (lengthDiff > 0) {
      // remove unused sheets
      const unused = sheetArray.value.splice(dataLength - 1, lengthDiff);
      unused.forEach(x => jss.removeStyleSheet(x.sheet as StyleSheet<string>));
    } else if (lengthDiff < 0) {
      // add new sheets
      for (let i = 0; i > lengthDiff; i--) {
        const sheet = jss.createStyleSheet(styles, { link: true });
        sheetArray.value.push({
          sheet
        });
        sheet.attach();
      }
    }

    // then apply new data to each stylesheet
    sheetArray.value.forEach((x, i) => {
      x.sheet.update(newData[i]);
    });
  }

  const stopWatch: () => void = watch(
    () => data.value,
    newValue => {
      updateData(newValue);
    },
    {
      deep: true
    }
  );

  function unsubscribe(): void {
    sheetArray.value.forEach(x => {
      jss.removeStyleSheet(x.sheet as StyleSheet<string>);
    });
    if (stopWatch) {
      stopWatch();
    }
  }

  onBeforeMount(() => {
    updateData(data.value);
  });

  onUnmounted(() => {
    unsubscribe();
  });

  const classes: Readonly<Ref<readonly Record<Name, string>[]>> = computed(() =>
    sheetArray.value.map(x => x.sheet.classes)
  );

  return { classes, unsubscribe };
}

/**
 * This generates the JSS style config needed from the styling props that you have
 * defined on the component.
 * @param styles {Object}
 */
export function createUseStylesConfig<
  PropType extends Record<string, string | number>
>(styleProps: Record<string, string | number>) {
  const config: Record<string, (props: PropType) => string | number> = {};

  Object.keys(styleProps).forEach(
    (key: string) => (config[key] = (props: PropType) => props[key])
  );

  return config;
}
